import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BrandLogo } from "@/components/BrandLogo";
import { landingForUser } from "@/lib/roleRedirect";

/** Jun 2026 — optional low-friction completion step after a NEW social
 *  signup (routed here by AuthComplete when new=1). Everything is
 *  skippable; the Customer account already exists either way. */
export default function AccountWelcome() {
  const { user, refresh } = useAuth();
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const done = () => navigate(user ? landingForUser(user) : "/customer", { replace: true });

  const save = async () => {
    setBusy(true);
    try {
      if (phone.trim()) await api.patch("/auth/me/profile", { phone });
      await refresh();
    } catch { /* optional step — never block */ }
    finally { done(); }
  };

  return (
    <div data-testid="account-welcome-page" className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4"><BrandLogo to="/" size="sm" /></div>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <PartyPopper className="w-8 h-8 text-cyan-600 mx-auto" />
            <h1 className="font-display mt-3 text-3xl font-bold text-slate-900">
              Welcome to AWARE{user ? `, ${user.full_name.split(" ")[0]}` : ""}
            </h1>
            <p className="mt-2 text-slate-600">Your customer account has been created. One optional detail helps us reach you about bookings and results:</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-200 p-7 shadow-sm space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Mobile <span className="normal-case text-slate-400">(optional)</span></Label>
              <Input data-testid="welcome-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="04XX XXX XXX" className="mt-1.5 h-12 rounded-xl" />
            </div>
            <Button data-testid="welcome-continue-btn" disabled={busy} onClick={save} size="lg" className="w-full btn-aqua rounded-full font-semibold">Continue</Button>
            <button data-testid="welcome-skip-btn" onClick={done} className="w-full text-xs font-semibold text-slate-500 hover:text-slate-700">Skip for now</button>
          </div>
        </div>
      </main>
    </div>
  );
}
